'use client'

import React from 'react'
import {
  Tag,
  Captions,
  Video,
  CheckCircle2,
  Circle,
  Eye,
  Sparkles,
  ArrowRight,
  Youtube,
} from 'lucide-react'
import BlogCTACard from '@/components/blog/BlogCTACard'

/**
 * YouTube AI Visibility Stack Diagrams
 * Visual support for the Gemini 2.5 + YouTube AI visibility blog post.
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 *
 * Theming rules per CLAUDE.md:
 *  - Site uses data-theme='dark' on <html>, NOT Tailwind dark: variants.
 *  - Use bg-muted/50, bg-background, bg-muted, bg-[var(--border)], text-foreground,
 *    text-foreground/80, text-muted-foreground. Never bg-white, bg-gray-*,
 *    text-gray-*, shadow-sm, or any dark: variant.
 */

interface LayerProps {
  number: number
  title: string
  subtitle: string
  description: string
  status: 'covered' | 'partial' | 'uncharted'
  color: string
  icon: React.ReactNode
}

const LAYERS: LayerProps[] = [
  {
    number: 1,
    title: 'Metadata',
    subtitle: 'Titles, descriptions, tags, chapters',
    description:
      'Indexed since the early days of LLM grounding. Every YouTube SEO playbook covers this layer.',
    status: 'covered',
    color: '#008dcc',
    icon: <Tag size={20} aria-hidden />,
  },
  {
    number: 2,
    title: 'Transcripts',
    subtitle: 'Auto-captions and uploaded transcripts',
    description:
      'AI engines have processed this layer since 2024. Treated as primary source for "what does X say about Y" prompts.',
    status: 'partial',
    color: '#8b5cf6',
    icon: <Captions size={20} aria-hidden />,
  },
  {
    number: 3,
    title: 'Raw Video',
    subtitle: 'Frames, on-screen text, visuals, gestures',
    description:
      'Effectively invisible before Gemini 2.5 (March 2026). Now retrievable as multimodal input. Almost nobody is auditing this layer.',
    status: 'uncharted',
    color: '#F90B8A',
    icon: <Video size={20} aria-hidden />,
  },
]

const STATUS_META = {
  covered: {
    label: 'Most brands cover this',
    icon: <CheckCircle2 size={14} aria-hidden />,
    badgeColor: '#22c55e',
  },
  partial: {
    label: 'Some brands cover this',
    icon: <Circle size={14} aria-hidden />,
    badgeColor: '#eab308',
  },
  uncharted: {
    label: 'Almost nobody covers this',
    icon: <Sparkles size={14} aria-hidden />,
    badgeColor: '#F90B8A',
  },
} as const

export function YouTubeAIStackDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-10 rounded-2xl border border-border/60 bg-muted/50 p-6 md:p-8'
    >
      <div className='mb-6 flex items-start gap-3'>
        <div
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg'
          style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}
        >
          <Eye size={18} aria-hidden />
        </div>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            The 3-Layer YouTube AI Visibility Stack
          </p>
          <p className='mt-1 text-sm leading-relaxed text-foreground/80'>
            How AI engines retrieve YouTube content. Each layer requires
            different optimization work. The third layer became real in March
            2026.
          </p>
        </div>
      </div>

      <div className='space-y-3'>
        {LAYERS.map(layer => {
          const status = STATUS_META[layer.status]
          return (
            <div
              key={layer.number}
              className='rounded-xl border border-border/60 bg-background p-5'
              style={{ borderLeftWidth: '4px', borderLeftColor: layer.color }}
            >
              <div className='flex flex-wrap items-center gap-3'>
                <div
                  className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold'
                  style={{
                    backgroundColor: `${layer.color}15`,
                    color: layer.color,
                  }}
                >
                  {layer.number}
                </div>
                <div className='flex items-center gap-2 text-foreground'>
                  {layer.icon}
                  <span className='text-base font-semibold leading-none'>
                    {layer.title}
                  </span>
                </div>
                <span
                  className='ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider'
                  style={{
                    backgroundColor: `${status.badgeColor}15`,
                    color: status.badgeColor,
                  }}
                >
                  {status.icon}
                  {status.label}
                </span>
              </div>
              <p className='mt-3 text-sm font-medium text-foreground/80'>
                {layer.subtitle}
              </p>
              <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                {layer.description}
              </p>
            </div>
          )
        })}
      </div>

      <div
        className='mt-5 rounded-lg p-4'
        style={{
          backgroundColor: '#F90B8A08',
          border: '1px solid #F90B8A20',
        }}
      >
        <p className='text-xs leading-relaxed text-foreground/80'>
          <span className='font-semibold' style={{ color: '#F90B8A' }}>
            The audit window:
          </span>{' '}
          Gemini 2.5 made Layer 3 retrievable in March 2026. The brands that
          build audit infrastructure for this layer first will define what
          "good" looks like inside the category. The opportunity is
          time-limited.
        </p>
      </div>
    </div>
  )
}

export function YouTubeBrandMonitorCTA() {
  return (
    <BlogCTACard
      href='/tools/youtube-brand-monitor'
      label='Run the audit'
      title='Score your brand on all 5 YouTube AI visibility dimensions'
      description='Volume, channel diversity, reach, sentiment, recency. Live YouTube Data API. Eighteen seconds. No card required.'
      buttonText='Audit my brand'
      ButtonIcon={ArrowRight}
      SideIcon={Youtube}
      gradient='from-[#d9097a] via-[#7c3aed] to-[#0077b3]'
      buttonColor='text-[#d9097a]'
    />
  )
}
